import "../Buttons/Buttons.scss";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Empty, Popconfirm, Skeleton, Switch, Typography } from "antd";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import SignatureCanvas from "react-signature-canvas";
import { apiClient } from "../../utils/httpClient";
import { UserSignature } from "../../utils/types";
import { Notify, getErrorMessage } from "../../utils/constants";
import { imageFileToPngDataUrl, SIGNATURE_UPLOAD_ACCEPT } from "../../utils/imageToPngDataUrl";

const { Text } = Typography;

const SIGNATURE_API = `${process.env.REACT_APP_API_URL}/users/me/signature`;

const MySignatureCard = () => {
  const [signature, setSignature] = useState<UserSignature | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const loadSignature = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<UserSignature>(SIGNATURE_API);
      setSignature(response.data);
    } catch (error) {
      console.error("Failed to load signature:", error);
      Notify("error", getErrorMessage(error, "Could not load your signature."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSignature();
  }, [loadSignature]);

  useEffect(() => {
    if (!isDrawing) return;

    const raf = requestAnimationFrame(() => {
      const wrap = wrapRef.current;
      const sig = sigCanvasRef.current;
      if (!wrap || !sig) return;

      const rect = wrap.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      const canvas = sig.getCanvas();
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);

      const ctx = canvas.getContext("2d");
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      sig.clear();
    });

    return () => cancelAnimationFrame(raf);
  }, [isDrawing]);

  const saveSignatureImage = async (dataUrl: string) => {
    setSaving(true);
    try {
      const response = await apiClient.put<UserSignature>(SIGNATURE_API, {
        signatureImage: dataUrl,
      });
      setSignature(response.data);
      setIsDrawing(false);
      Notify("success", "Signature saved successfully!");
    } catch (error) {
      console.error("Failed to save signature:", error);
      Notify("error", getErrorMessage(error, "Could not save your signature."));
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      const dataUrl = await imageFileToPngDataUrl(file);
      await saveSignatureImage(dataUrl);
    } catch (error) {
      Notify(
        "error",
        error instanceof Error
          ? error.message
          : "Unsupported file. Please upload a jpg, jpeg, png, gif or bmp image.",
      );
    }
  };

  const handleSaveDrawnSignature = () => {
    const sig = sigCanvasRef.current;
    if (!sig || sig.isEmpty()) {
      Notify("error", "Please draw a signature before saving.");
      return;
    }
    saveSignatureImage(sig.getTrimmedCanvas().toDataURL("image/png"));
  };

  const handleDelete = async () => {
    try {
      const response = await apiClient.delete<UserSignature>(SIGNATURE_API);
      setSignature(response.data);
      Notify("success", "Signature deleted successfully!");
    } catch (error) {
      console.error("Failed to delete signature:", error);
      Notify("error", getErrorMessage(error, "Could not delete your signature."));
    }
  };

  const handleToggleChange = async (checked: boolean) => {
    setSaving(true);
    try {
      const response = await apiClient.put<UserSignature>(`${SIGNATURE_API}/toggle`, {
        useSignatureAuto: checked,
      });
      setSignature(response.data);
    } catch (error) {
      console.error("Failed to update signature toggle:", error);
      Notify("error", getErrorMessage(error, "Could not update this setting."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card title="My signature" className="profile-card">
        <Skeleton active paragraph={{ rows: 3 }} />
      </Card>
    );
  }

  return (
    <Card title="My signature" className="profile-card">
      <div className="my-signature">
        {signature?.signatureImage ? (
          <>
            <div className="my-signature__preview">
              <img src={signature.signatureImage} alt="Your saved signature" />
            </div>

            <div className="my-signature__toggle">
              <div>
                <Text strong>Use this signature</Text>
                <br />
                <Text type="secondary">
                  Automatically sign every request you create or approve.
                </Text>
              </div>
              <Popconfirm
                title="Use this signature automatically?"
                description="This image will be used to sign every request you create or approve from now on, until you turn this off."
                okText="Yes, use it"
                cancelText="Cancel"
                disabled={signature.useSignatureAuto}
                onConfirm={() => handleToggleChange(true)}
              >
                <Switch
                  checked={signature.useSignatureAuto}
                  loading={saving}
                  onChange={(checked) => {
                    if (!checked) handleToggleChange(false);
                  }}
                />
              </Popconfirm>
            </div>

            <div className="my-signature__actions">
              <Popconfirm
                title="Delete this signature?"
                description="You will need to upload or draw a new one before using it again."
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={handleDelete}
              >
                <Button danger icon={<DeleteOutlined />}>
                  Delete
                </Button>
              </Popconfirm>
            </div>
          </>
        ) : isDrawing ? (
          <>
            <div className="sign-canvas-wrapper" ref={wrapRef}>
              <SignatureCanvas
                penColor="black"
                ref={sigCanvasRef}
                canvasProps={{ className: "sigCanvas" }}
              />
            </div>
            <div className="my-signature__actions">
              <Button onClick={() => setIsDrawing(false)}>Cancel</Button>
              <Button onClick={() => sigCanvasRef.current?.clear()}>Clear</Button>
              <Button type="primary" loading={saving} onClick={handleSaveDrawnSignature}>
                Save as My Signature
              </Button>
            </div>
          </>
        ) : (
          <>
            <Empty
              description="No signature saved yet"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
            <div className="my-signature__choices">
              <input
                ref={fileInputRef}
                type="file"
                accept={SIGNATURE_UPLOAD_ACCEPT}
                style={{ display: "none" }}
                onChange={handleFileSelected}
              />
              <Button
                icon={<UploadOutlined />}
                loading={saving}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload signature image
              </Button>
              <Button onClick={() => setIsDrawing(true)}>Draw signature</Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

export default MySignatureCard;
